import { useRef, useState } from "react";
import { Loader2, Search, Upload, ShieldAlert, ShieldCheck, HelpCircle, FileQuestion } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

const TONE = {
  identified: ["#e5484d", ShieldAlert, "Identified"],
  likely: ["#f5a524", ShieldAlert, "Likely"],
  unmatched: ["#9aa0a8", HelpCircle, "Template found, no matching shop"],
  unreadable: ["#9aa0a8", HelpCircle, "Template found, fingerprint unreadable"],
  no_template: ["#6b7079", FileQuestion, "Not one of our templates"],
  invalid: ["#6b7079", FileQuestion, "Invalid outline"],
};

const Result = ({ o, i }) => {
  const [color, Icon, label] = TONE[o.status] || TONE.invalid;
  return (
    <div className="glass-row px-3.5 py-2.5 text-[12px]" style={{ borderLeft: `3px solid ${color}` }} data-testid={`leak-result-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="h-4 w-4" style={{ color }} /><b style={{ color }}>{label}</b>
        {o.template && <span className="text-[#c7cbd1]">· {o.template.vehicle} — {o.template.name} <span className="text-[#6b7079]">({o.template.source}, {o.points} pts, fit {o.template.rms}")</span></span>}
      </div>
      {o.match && <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid={`leak-match-${i}`}>
        <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: `${color}22`, color }}>{o.match.confidence}% · {o.match.agree}/{o.match.decidable} bits{o.match.fit_gain ? ` · fit ×${o.match.fit_gain}` : ""}</span>
        <span className="font-medium text-[#e8e9ec]">{o.match.shop_name}</span><span className="text-[#9aa0a8]">{o.match.email}</span>
      </div>}
      {o.reason && <div className="mt-1 text-[11px] text-[#9aa0a8]">{o.reason}</div>}
      {o.candidates?.length > 1 && <div className="mt-1 text-[10.5px] text-[#6b7079]">Other shops: {o.candidates.slice(o.match ? 1 : 0, 4).map((c) => `${c.shop_name || c.email} ${c.confidence}%`).join(" · ")}</div>}
    </div>
  );
};

export const IdentifyLeak = () => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    try { setRes(await api.adminIdentifyLeak(file)); } catch (e) { toast.error(apiError(e, "Could not read that file")); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="glass-row p-3" data-testid="identify-leak">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold"><Search className="h-3.5 w-3.5 text-[#e5484d]" /> Identify a leaked template</div>
        <span className="text-[11px] text-[#6b7079]">Every outline served to a shop carries an invisible ±0.0015" fingerprint. Upload the leaked file (SVG, DXF, PLT/HPGL, PDF, EPS) and it is traced back to the account.</span>
        <input ref={ref} type="file" accept=".svg,.dxf,.plt,.hpgl,.hgl,.pdf,.eps,.ai" className="hidden" data-testid="leak-file-input" onChange={(e) => run(e.target.files?.[0])} />
        <button data-testid="leak-upload-btn" disabled={busy} onClick={() => ref.current?.click()} className="glass-btn ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-medium">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {busy ? "Analysing…" : "Upload leaked file"}</button>
      </div>
      {res && <div className="mt-3 space-y-1.5" data-testid="leak-results">
        <div className="text-[11px] text-[#9aa0a8]"><ShieldCheck className="mr-1 inline h-3 w-3 text-[#3ecf8e]" />{res.file} · {res.outlines.length} outline{res.outlines.length === 1 ? "" : "s"} analysed</div>
        {res.outlines.map((o, i) => <Result key={i} o={o} i={i} />)}
      </div>}
    </div>
  );
};
